import * as vp from 'vopay';
import { Box, Title, Button } from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { FormikErrors, FormikTouched } from 'formik';
import useFetchWorkflows from '@/hooks/useApi/useFetchWorkflows';
import useFetchTransactionTypes from '@/hooks/useApi/useFetchTransactionTypes';
import UpdateWorkFlowForm, { UpdateWorkflow, UpdateWorkflowContainer } from './UpdateWorkFlowForm';
import WorkflowContext, { WorkflowCtx } from '../Dialogue/WorkflowContext';
import WorkflowDialogue from '../Dialogue/WorkflowDialogue';
import { useApprovalWorkflow } from '@/providers/ApprovalWorkflow.provider';
import { WorkflowDialogueHandler, DialogueHandlerProps } from '../Dialogue/types';
import usePostUpdateWorkflow from '@/hooks/useApi/usePostUpdateWorkflow';
import usePostCreateWorkflow from '@/hooks/useApi/usePostCreateWorkflow';
import useFetchUserRoles from '@/hooks/useApi/useFetchUserRoles';
import { useTranslation } from 'react-i18next';
import { useClientData } from '@/providers/ClientData.provider';
import { UtilButton } from 'UI';

type FC = FunctionComponent;

const UpdateWorkFlowContainer: FC = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const isCreate = !id;
  const [isView, setIsView] = useState(!isCreate);
  const [workflowsResponse, fetchWorkflows] = useFetchWorkflows({});
  const [transactionTypesResponse, fetchTransactionTypes] = useFetchTransactionTypes({});
  const [userRolesResponse, fetchUserRoles] = useFetchUserRoles({});
  const [workflowsList, setWorkFlowList] = useState<(UpdateWorkflowContainer | undefined)[]>([undefined]);
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [, , updateWorkflowAsync] = usePostUpdateWorkflow({});
  const [, , createWorkFlowAsync] = usePostCreateWorkflow({});
  const { approvalListReset, transactionsReset, areEnoughApprovers, getPaymentRail } = useApprovalWorkflow();
  const [hasAddNew, setHasNew] = useState(true);
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { t } = useTranslation('translations', { keyPrefix: 'TransactionApproval.Configuration.Workflows.Update' });
  const {
    response: { Country },
  } = useClientData();
  useEffect(() => {
    if (workflowsList && workflowsResponse) {
      const workflowsAdded = workflowsList.map((workflow) => workflow?.values?.TransactionTypes || '').join('|');
      const workflowsExistitng = Object.values(workflowsResponse?.Workflows || {})
        .map((workflow) => getPaymentRail(workflow.VIOAccountTransactionTypes_ID || 0, transactionTypesResponse))
        .join('|');
      const workflowAll = `${workflowsAdded}|${workflowsExistitng}`.split('|');
      const filteredWorkflow = Object.values(transactionTypesResponse?.TransactionTypes || {}).filter(
        (transactionType) => {
          const exists = workflowAll.some((workflow) => workflow === transactionType.TransactionType);
          return !exists;
        },
      );
      setHasNew(filteredWorkflow.length > 0);
    }
  }, [workflowsList, workflowsResponse, transactionTypesResponse]);

  const parseWorkflows = (workflows: vp.WorkFlowsResponse | undefined) => {
    const newWorkflow = Object.values(workflows?.Workflows || {})
      .map((workflow) => ({
        values: {
          ...workflow,
          TransactionTypes: getPaymentRail(workflow.VIOAccountTransactionTypes_ID || 0, transactionTypesResponse),
        },
      }))
      .filter((workflow) => workflow.values.ID.toString() === id);

    setWorkFlowList(newWorkflow);
  };
  useEffect(() => {
    if (id) {
      parseWorkflows(workflowsResponse);
    }
  }, [workflowsResponse, id, transactionTypesResponse]);
  useEffect(() => {
    fetchWorkflows({});
    fetchUserRoles({
      Role: 'approver',
    });
    fetchTransactionTypes({
      IsWithdraw: 1,
      CountriesID: Country === 'CA' ? 1 : 2,
      IsTransactionApprovalSupported: 1,
    });
  }, []);
  const [showWorkflowSave, setShowWorkflowSave] = useState(false);
  const [showWorkflowCreate, setShowWorkflowCreate] = useState(false);
  const workflowContext: WorkflowCtx = {
    workflowError: '',
    workflowName: '',
    showWorkflowErrorAlert: false,
    showWorkflowActivate: false,
    showWorkflowDeactivate: false,
    showWorkflowSave,
    showWorkflowCreate,
    workflowDescription,
  };
  const handlers = {
    handleWorkflowDialogue: (handlerProps: DialogueHandlerProps<WorkflowDialogueHandler>) => {
      const { type: typeHandler, state: state2, action = 'default' } = handlerProps;

      const dialogueHandlers: WorkflowDialogueHandler = {
        error: {
          confirm: {
            default: () => {},
          },
          cancel: {
            default: () => {},
          },
          close: {
            default: () => {},
          },
        },
        activate: {
          confirm: {
            default: () => {
              // show toast and activate
            },
          },
          cancel: {
            default: () => {},
          },
          close: {
            default: () => {},
          },
        },
        deactivate: {
          confirm: {
            default: () => {
              // show toast amd decactove
            },
          },
          cancel: {
            default: () => {},
          },
          close: {
            default: () => {},
          },
        },
        save: {
          confirm: {
            default: () => {
              setShowWorkflowSave(false);
              const updateWorkflows = async () => {
                for (const workflow of workflowsList) {
                  if (workflow) {
                    await updateWorkflowAsync({
                      WorkflowID: workflow?.values.ID || 0,
                      NumberApprovalsRequired: workflow?.values.NumberApprovalsRequired || 0,
                      MinAmount: workflow?.values.MinAmount || 0,
                    });
                  }
                }
                transactionsReset();
                approvalListReset();
                history.push({
                  pathname: '/transaction-approval/configuration/workflows',
                  state: {
                    isCreate: true,
                    hasCreateMultiple: workflowsList.length > 1,
                    isUpdate: !isCreate,
                    numberCreated: workflowsList.length,
                    paymentRailName: !isCreate
                      ? getPaymentRail(
                          workflowsList[0]?.values.VIOAccountTransactionTypes_ID || 0,
                          transactionTypesResponse,
                        )
                      : '',
                  },
                });
              };
              void updateWorkflows();
            },
          },
          cancel: {
            default: () => {
              history.push('/transaction-approval/configuration/roles');
            },
          },
          close: {
            default: () => {
              setShowWorkflowSave(false);
            },
          },
        },
        create: {
          confirm: {
            default: () => {
              setShowWorkflowCreate(false);
              const createWorkflows = async () => {
                let numberCreated = 0;
                for (const workflow of workflowsList) {
                  if (workflow) {
                    const types = workflow?.values.TransactionTypes.split('|') || [];
                    for (const transactionType in types) {
                      await createWorkFlowAsync({
                        TransactionType: types[transactionType] || '',
                        NumberApprovalsRequired: workflow?.values.NumberApprovalsRequired || 0,
                        MinAmount: workflow?.values.MinAmount || 0,
                      });
                      numberCreated += 1;
                    }
                  }
                }
                transactionsReset();
                approvalListReset();
                history.push({
                  pathname: '/transaction-approval/configuration/workflows',
                  state: {
                    isCreate: true,
                    hasCreateMultiple: workflowsList.length > 1,
                    isUpdate: !isCreate,
                    numberCreated,
                    paymentRailName: !isCreate
                      ? getPaymentRail(
                          workflowsList[0]?.values.VIOAccountTransactionTypes_ID || 0,
                          transactionTypesResponse,
                        )
                      : '',
                  },
                });
              };
              void createWorkflows();
            },
          },
          cancel: {
            default: () => {
              history.push('/transaction-approval/configuration/roles');
            },
          },
          close: {
            default: () => {
              setShowWorkflowCreate(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[typeHandler][state2];
      const handler = dialogueState ? dialogueState[action] || dialogueState.default : () => {};

      handler();
    },
  };
  return (
    <div>
      <WorkflowContext.Provider value={workflowContext}>
        <Box flx="col" mb="md" gapSize="16px">
          <UtilButton
            iconLeft="chevron_left"
            size="large"
            onClick={() => {
              if (history.length > 1) {
                history.goBack();
              } else {
                history.replace('/transaction-approval/configuration/workflows');
              }
            }}
          >
            {history.length > 1 ? tGlobal('Buttons.back') : t('backButton')}
          </UtilButton>

          <Title title={isCreate ? t('createTitle') : isView ? t('viewTitle') : t('editTitle')} />
        </Box>
        <Box mt="md" mb="lg" flx="col" gap="lg" width="100%">
          {workflowsList.map((workflow, index) => (
            <Box width="100%">
              <UpdateWorkFlowForm
                id={index}
                defaultValues={workflow?.values}
                isView={isView}
                onChange={(
                  FormId: number,
                  values: UpdateWorkflow,
                  errors: FormikErrors<UpdateWorkflow>,
                  touched: FormikTouched<UpdateWorkflow>,
                  submitForm: () => Promise<void>,
                ) => {
                  const workflowListNew = [...workflowsList];
                  workflowListNew[FormId] = {
                    values: { ...values },
                    errors,
                    touched,
                    submitForm,
                  };
                  setWorkFlowList(workflowListNew);
                }}
                workflowList={workflowsList}
                onDelete={(idDelete: number) => {
                  setWorkFlowList(workflowsList.filter((_, i) => i !== idDelete));
                }}
                isDelete={index > 0}
                isCreate={isCreate}
                workflowsResponse={workflowsResponse}
                transactionTypesResponse={transactionTypesResponse}
              />
            </Box>
          ))}
        </Box>
        {isCreate && hasAddNew && (
          <Box mt="md" mb="md" jc="fs">
            <Button
              id="add-new-workflow"
              label={t('Buttons.addNewWorkflow')}
              color="secondary"
              icon="add"
              iconLocation="left"
              iconHeight="12px"
              iconWidth="12px"
              onClick={() => {
                setWorkFlowList([...workflowsList, undefined]);
              }}
            />
          </Box>
        )}
        <Box jc="fe">
          <Box gap="sm">
            {!isView && (
              <>
                {!isCreate && (
                  <Button
                    id="approval-cancel-workflow"
                    label={t('Buttons.cancel')}
                    color="secondary"
                    onClick={() => {
                      parseWorkflows(workflowsResponse);
                      setIsView(true);
                    }}
                  />
                )}
                <Button
                  id="approval-update-roles"
                  label={isCreate ? t('Buttons.createWorkflow') : t('Buttons.save')}
                  isDisabled={workflowsList.some(
                    (workflow) => workflow?.errors && Object.keys(workflow.errors).length > 0,
                  )}
                  color="primary"
                  onClick={() => {
                    const numberOfError = workflowsList.reduce((accumulator, currentValue) => {
                      if (currentValue && currentValue.errors) {
                        return accumulator + Object.keys(currentValue.errors).length;
                      }
                      return accumulator;
                    }, 0);
                    let description = '';
                    workflowsList.forEach((workflow) => {
                      if (
                        !areEnoughApprovers(
                          workflow?.values.MinAmount,
                          workflow?.values.TransactionTypes,
                          workflow?.values.NumberApprovalsRequired,
                          userRolesResponse,
                        )
                      ) {
                        description = t('Buttons.approversValidationError');
                      }
                    });
                    setWorkflowDescription(description);

                    if (numberOfError > 0) {
                      workflowsList.forEach((workflow) => {
                        if (workflow && workflow.submitForm) {
                          void workflow.submitForm();
                        }
                      });
                    } else if (isCreate) {
                      setShowWorkflowCreate(true);
                    } else {
                      setShowWorkflowSave(true);
                    }
                  }}
                />
              </>
            )}
            {isView && (
              <Button
                id="approval-edit-workflow"
                label={t('Buttons.editWorkflow')}
                color="primary"
                onClick={() => {
                  setIsView(false);
                }}
              />
            )}
          </Box>
        </Box>
        <WorkflowDialogue handleDialogue={handlers.handleWorkflowDialogue} />
      </WorkflowContext.Provider>
    </div>
  );
};
export default UpdateWorkFlowContainer;
